/* KANON view 05's foot, measured off `geometry-05-sesja-panel.json`.
 *
 * The block sits on the panel's own white, separated by a rule at the TOP and nothing else — it is
 * the last thing in a column, so it needs no edge of its own below or beside.
 *
 * Every number below is the canon's, and every one of them arrives through a token. Where the canon
 * writes a value the scale does not hold, the token was added rather than the literal written; see
 * `tokens/spacing.css` for the per-pixel steps this app already carries. */

.composer {
  display: flex;
  flex-direction: column;
  gap: var(--space-9px);
  padding: var(--space-13px) var(--space-5) var(--space-15px);
  background: var(--bg-card);
  border-top: var(--border-hairline) solid var(--border-2);
}

.head {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
}

/* The canon sets this at the panel's own body size and regular weight rather than as a tracked
 * eyebrow — it is a field LABEL, and it reads as one. */
.eyebrow {
  flex: 1;
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text);
  cursor: pointer;
}

/* `--text-2`, where the canon writes `--text-3`. The repository's standing deviation: `--text-3`
 * measures 2.17–2.80 against these surfaces and fails AA at every size the canon uses it, and this
 * line tells the trainer who else will read what they are about to write. Recorded in
 * `docs/design/kanon-clients-2026-08-11/README.md`. */
.visibility {
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.field {
  min-height: var(--h-comment-field);
  padding: var(--space-11px) var(--space-3);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text);
  resize: vertical;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
}

.field::placeholder {
  color: var(--text-2);
}

.field:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.foot {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.note {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

/* The ghost beside the primary. Its geometry matches the primary's exactly so the pair sits on one
 * baseline; only the fill differs. */
.cancel {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-comment-action);
  padding: 0 var(--space-13px);
  font-family: var(--font-body);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  cursor: pointer;
  background: none;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
}

.cancel:disabled {
  /* The label stays readable — `--text-3` measured 2.86:1 on the light card, and the button rests
   * disabled whenever there is nothing to undo, which is most of the time. The quieter outline and
   * the cursor say it is inactive; `disabled` says it to assistive technology. */
  color: var(--text-2);
  border-color: var(--border);
  cursor: not-allowed;
}

.cancel:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* Geometry only — the fill, the ink and the three states come from `shared/ui/inkButton`. */
.save {
  height: var(--h-comment-action);
  padding: 0 var(--space-13px);
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  letter-spacing: normal;
  border-radius: var(--radius-sm);
}

/* The save's confirmation, and it is SEEN as well as announced.
 *
 * It used to be visually hidden, so the only feedback a sighted trainer got from a successful save
 * was both controls going quiet — which is indistinguishable from a save that never fired. The
 * composer stays open and both controls refuse once the draft matches what the server holds, so
 * this is a resting state rather than a toast: it needs no timer, and it clears the moment the
 * trainer types again.
 *
 * `--good-ink` and this size are `shared/ui/FormField`'s `.messageGood` — the app already had one
 * way to say "that worked" under a field, and a second one two pixels different is the near
 * duplicate the house rule exists to prevent. */
.status {
  margin: 0;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--good-ink);
}

/* Empty, but PRESENT. A `role="status"` inserted into the document together with its text is not
 * reliably announced — the same reason `features/assign-plan`'s count and the progression drawer's
 * refusal are both always mounted. Collapsed to nothing so it costs no space while it says nothing. */
.statusEmpty {
  height: 0;
  overflow: hidden;
}

.error {
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--danger-text-strong);
}

/* The host's quick replies — pill buttons that start the draft (KANON "Karty zadań" `.k-draft`). */
.drafts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
}

.draft {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: var(--space-0) var(--space-10px);
  font-family: var(--font-body);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  cursor: pointer;
  background: var(--bg-chip);
  border-style: none;
  border-radius: var(--r-pill);
}

.draft:hover {
  color: var(--text);
}

.draft:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The task card's shape — see the `compact` prop. */
.compact {
  min-height: 0;
  gap: var(--space-6px);
  padding: var(--space-2) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

/* One tight line: "Komentarz do sesji · Klient zobaczy go w aplikacji". The dot is drawn, not said —
 * the label and the visibility line are read as two, as they are in the panel. */
.compact .head {
  gap: var(--space-6px);
}

.compact .eyebrow {
  flex: none;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
}

.compact .visibility::before {
  margin-inline-end: var(--space-6px);
  content: "·" / "";
}

.compact .field {
  min-height: var(--h-comment-field-compact);
  padding-block: var(--space-6px);
  line-height: var(--lh-note);
  background: var(--bg-card);
}

.compact .note:empty {
  display: none;
}

/* The quick replies take the line's free width, so the two controls stand at its end. */
.compact .drafts {
  flex: 1;
}

/* An empty status still spends the section's gap; in the card that gap is the fold. */
.compact .statusEmpty {
  display: none;
}
